import { CheckOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import {
  Button,
  Dropdown,
  Input,
  Menu,
  Row,
  Skeleton,
  Tag,
  Tooltip,
} from "antd";
import Checkbox from "antd/lib/checkbox/Checkbox";
import axios from "axios";
import { useField, useFormikContext } from "formik";
import { any, pathOr, pick } from "ramda";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { VariableSizeList } from "react-window";
import { DEBOUNCE_TIME, DROPDOWN_SIZE } from "../../constants/constants";
import { ErrorIcon, SearchIcon, SmallCloseIcon } from "../../assets/svg";
import {
  debounceAsync,
  ellipsize,
  generateId,
  getValueByKey,
} from "../../utilities/helpers";
import "./styles.scss";

const CustomMultiSelect = ({
  customClass,
  data: passedData = [],
  disabled = false,
  emptyText = "Items are not available at the moment",
  id,
  initialOptions = [],
  isItemDisabled,
  placeholder,
  loadMoreData,
  maxTags = 3,
  multiple = false,
  onBlur,
  onChange,
  optionLabelProp = "title",
  optionValueProp = "value",
  showCheckboxes = false,
  showAll = false,
  value,
  showSearch = true,
  searchColumns = [],
  extraData = [],
  allowClear,
}) => {
  const [state, setState] = useState({
    searchQuery: "",
    pageNumber: 2,
    loading: false,
    count: 0,
    searching: false,
    error: false,
    attached: false,
    uuid: generateId(10, true),
    dropdownVisible: false,
    selectedItems: multiple
      ? (
          value.map((v) =>
            [...initialOptions, ...passedData].find(
              (t) => getValueByKey(t, optionValueProp) === v
            )
          ) || []
        ).filter((item) => !!item)
      : [...initialOptions, ...passedData].find(
          (t) => getValueByKey(t, optionValueProp) === value
        ) || {},
    focused: false,
  });

  const searchColKeys = useMemo(() => [...searchColumns, optionLabelProp], [
    searchColumns,
    optionLabelProp,
  ]);

  const isAllVisible = useMemo(() => showAll && passedData.length > 1, [
    showAll,
    passedData.length,
  ]);

  const data = useMemo(
    () => {
      const sortedData = loadMoreData
        ? passedData
        : passedData.sort(
            (a, b) =>
              getValueByKey(a, optionLabelProp) >
              getValueByKey(b, optionLabelProp)
                ? 1
                : -1
          );

      const filterOptions = (option) =>
        any(
          (searchColKey) =>
            (getValueByKey(option, searchColKey) || "")
              .toLowerCase()
              .indexOf(state.searchQuery?.toLowerCase() || "") >= 0,
          searchColKeys
        );

      const initialOption = (initialOptions || []).filter(filterOptions);

      const allOption =
        isAllVisible && !state.searchQuery
          ? [{ [optionValueProp]: "all", [optionLabelProp]: "All", key: "all" }]
          : [];

      let allData;

      if (loadMoreData) {
        allData = [...initialOption, ...allOption, ...sortedData];
      } else {
        if (!state?.searchQuery)
          allData = [...initialOption, ...allOption, ...sortedData];
        else allData = [...initialOption, ...sortedData.filter(filterOptions)];
      }
      return allData;

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [passedData, state?.searchQuery || ""]
  );

  useEffect(
    () => {
      if (
        data.length &&
        (value === " " || value === "" || value?.length === 0 || !loadMoreData)
      )
        setState((st) => ({
          ...st,
          selectedItems: multiple
            ? value
                .map((v) =>
                  [...initialOptions, ...passedData].find(
                    (t) => getValueByKey(t, optionValueProp) === v
                  )
                )
                .filter((x) => !!x)
            : [...initialOptions, ...passedData].find(
                (t) => getValueByKey(t, optionValueProp) === value
              ),
        }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, data.length, passedData]
  );
  useEffect(
    () => {
      if (!loadMoreData)
        setState((st) => ({
          ...st,
          count: data.length,
          selectedItems: multiple
            ? value.map((v) =>
                passedData.find((t) => getValueByKey(t, optionValueProp) === v)
              ) || []
            : passedData.find(
                (t) => getValueByKey(t, optionValueProp) === value
              ) || {},
        }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.length]
  );

  const inputRef = useRef();
  const reachedBottom = useRef(false);
  const listRef = useRef();

  useEffect(
    () => {
      if (loadMoreData && state.searchQuery === "") {
        setState((st) => ({ ...st, loading: true }));
        loadMoreData(1)
          .then((count) => {
            setState((st) => ({
              ...st,
              error: false,
              loading: false,
              searching: false,
              pageNumber: 2,
              count,
            }));
          })
          .catch((error) => {
            const requestCancelled = !axios.isCancel(error);
            setState((st) => ({
              ...st,
              loading: false,
              searching: false,
              error: requestCancelled,
            }));
          });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [state.searchQuery]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadMoreDataDebounced = useCallback(
    debounceAsync(loadMoreData, DEBOUNCE_TIME, false),
    [loadMoreData]
  );
  const stateRef = useRef({});
  stateRef.current = state;

  const isEmptyRef = useRef();
  isEmptyRef.current = state.error || data.length === 0;
  const handleScroll = useCallback(
    async (listContainer) => {
      if (listContainer) {
        const state = stateRef.current;

        const bottom =
          Math.floor(listContainer.scrollHeight - listContainer.scrollTop) ===
          listContainer.clientHeight;
        const hasMoreData = state.count
          ? state.pageNumber <=
            Math.ceil((state.count + (isAllVisible ? 1 : 0)) / DROPDOWN_SIZE)
          : true;

        if (
          !reachedBottom.current &&
          ((bottom && hasMoreData) || state.error)
        ) {
          reachedBottom.current = true;
          setState((st) => ({ ...st, loading: true, error: false }));
          loadMoreDataDebounced(state.pageNumber, state.searchQuery, true)
            .then((count) => {
              setState((st) => ({
                ...st,
                count,
                loading: false,
                error: false,
              }));

              reachedBottom.current = false;
              setState((st) => ({ ...st, pageNumber: st.pageNumber + 1 }));
            })
            .catch((error) => {
              const requestCancelled = !axios.isCancel(error);
              setState((st) => ({
                ...st,
                loading: false,
                error: requestCancelled,
              }));
              reachedBottom.current = false;
            });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.count, state.pageNumber, state.searchQuery]
  );

  const handleSearch = (e) => {
    const searchQuery = e.target.value;
    setState((st) => ({
      ...st,
      pageNumber: 2,
      searchQuery,
      loading: loadMoreData ? true : false,
      searching: true,
    }));

    const finalRef = () => {
      if (listRef.current) {
        listRef.current.resetAfterIndex(0);
        listRef.current.scrollTo(data.length - 1);
      }
    };

    loadMoreData &&
      loadMoreDataDebounced(1, searchQuery, false)
        .then((count) => {
          setState((st) => ({
            ...st,
            error: false,
            loading: false,
            searching: false,
            dropdownVisible: true,
            count,
          }));
        })
        .catch((error) => {
          const requestCancelled = !axios.isCancel(error);
          setState((st) => ({
            ...st,
            loading: false,
            searching: false,
            error: requestCancelled,
          }));
        })
        .finally(() => {
          finalRef();
        });

    reachedBottom.current = false;
  };
  const onScroll = (e) => {
    handleScroll(e.target);
  };
  const onVisibleChange = (visible) => {
    if (!visible) {
      const menu = document.getElementById(state.uuid);
      menu.scrollTop = 0;
    }
    setState((st) => ({ ...st, dropdownVisible: visible }));
  };
  useEffect(
    () => {
      if (state.dropdownVisible) {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      } else {
        setState((st) => ({
          ...st,
          searchQuery: st.focused ? st.searchQuery : "",
        }));
      }
    },
    [state.dropdownVisible, state.focused]
  );

  const outerElementType = useMemo(
    () => {
      return forwardRef((props, ref) => {
        return (
          <Menu
            id={state.uuid}
            ref={ref}
            {...props}
            style={{
              ...(props.style || {}),
              zIndex: 99999,
              pointerEvents: "auto",
              minHeight: isEmptyRef.current ? "150px" : undefined,
            }}
            className="custom-select-menu"
            onScroll={
              loadMoreData
                ? (e) => {
                    props.onScroll && props.onScroll(e);
                    onScroll(e);
                  }
                : props.onScroll
            }
            onWheel={
              loadMoreData
                ? (e) => {
                    props.onWheel && props.onWheel(e);
                    onScroll(e);
                  }
                : props.onWheel
            }
          />
        );
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getItemSize = (index) => {
    const state = stateRef.current;
    const isLast = index === (data?.length || 0);
    const isEmpty = index === 0 && data.length === 0 && !state.error;
    if (isEmpty) {
      return 175;
    }
    if (state.loading && isLast) {
      return 100;
    } else if (state.error && isLast) {
      return 40;
    }
    return 40;
  };

  const isValuePresent = multiple ? value.length > 0 : !!value;
  return (
    <Dropdown
      disabled={disabled}
      trigger={["click"]}
      className="multi-select-dropdown"
      visible={state.dropdownVisible}
      onVisibleChange={onVisibleChange}
      getPopupContainer={(trigger) => trigger.parentNode}
      overlay={
        <VariableSizeList
          ref={listRef}
          height={200}
          itemSize={getItemSize}
          outerElementType={outerElementType}
          onItemsRendered={
            loadMoreData ? () => listRef.current?.resetAfterIndex(0) : undefined
          }
          itemCount={Math.max(
            1,
            data.length + (state.loading ? 1 : 0) + (state.error ? 1 : 0)
          )}
        >
          {({ style, index }) => {
            const isEmpty = data.length === 0 && index === 0 && !state.error;
            const isLast = index === Math.max(0, (data?.length || 0) - 1);

            if (isEmpty) {
              return (
                <Menu.Item
                  style={style}
                  key="empty"
                  value="empty"
                  className="unclickable-option empty-slate"
                >
                  <Row className="error-icon" justify="center" align="middle">
                    <ErrorIcon />
                  </Row>
                  <Row
                    style={{ marginTop: 16 }}
                    justify="center"
                    align="middle"
                  >
                    <p
                      style={{
                        width: "100%",
                        whiteSpace: "normal",
                        lineHeight: "2rem",
                        textAlign: "center",
                      }}
                    >
                      {emptyText}
                    </p>
                  </Row>
                </Menu.Item>
              );
            }

            if (state.loading && isLast) {
              return (
                <Menu.Item
                  key={"loading" + index}
                  title="Loading..."
                  value="loading"
                  style={style}
                  className="unclickable-option menu-item"
                >
                  <div>
                    <Skeleton
                      active
                      round
                      paragraph={{ rows: 4, width: ["100%", "80%", "50%"] }}
                      className="dropdown-loader"
                    />
                  </div>
                </Menu.Item>
              );
            } else if (state.error && isLast) {
              return (
                <>
                  <Menu.Item
                    key="error"
                    title="Error loading data!"
                    value="error"
                    style={style}
                    className="unclickable-option menu-item"
                  >
                    <Row className="error-icon" justify="center" align="middle">
                      <ErrorIcon color="#cb2e31" />
                    </Row>
                    <Row
                      style={{ marginTop: 16 }}
                      justify="center"
                      align="middle"
                    >
                      <p
                        style={{
                          width: "100%",
                          whiteSpace: "normal",
                          lineHeight: "2rem",
                          textAlign: "center",
                          color: "black",
                        }}
                      >
                        {" "}
                        <div>Error loading data</div>
                        <Button
                          onMouseDown={() => {
                            setState((st) => ({
                              ...st,
                              loading: true,
                            }));
                            loadMoreDataDebounced(
                              state.pageNumber,
                              state.searchQuery,
                              true
                            )
                              .then((count) => {
                                setState((st) => ({
                                  ...st,
                                  count,
                                  loading: false,
                                  error: false,
                                }));

                                reachedBottom.current = false;
                              })
                              .catch((error) => {
                                const requestCancelled = !axios.isCancel(error);
                                setState((st) => ({
                                  ...st,
                                  loading: false,
                                  error: requestCancelled,
                                }));
                                reachedBottom.current = false;
                              });
                          }}
                          type="link"
                        >
                          Try Again
                        </Button>
                      </p>
                    </Row>
                  </Menu.Item>
                </>
              );
            }

            const datum = getValueByKey(data, Math.max(0, index));
            if (!datum) return <div />;

            const isAll = getValueByKey(datum, optionValueProp) === "all";
            const checked = multiple
              ? value.includes(getValueByKey(datum, optionValueProp))
              : value === getValueByKey(datum, optionValueProp);
            const onItemChange = () => {
              if (multiple) {
                if (checked) {
                  const selectedItems = state.selectedItems.filter(
                    (item) =>
                      getValueByKey(item, optionValueProp) !==
                      getValueByKey(datum, optionValueProp)
                  );
                  onChange(
                    value.filter(
                      (v) => v !== getValueByKey(datum, optionValueProp)
                    ),
                    selectedItems
                  );
                  setState((st) => {
                    return {
                      ...st,
                      selectedItems,
                    };
                  });
                } else {
                  const selectedItems = isAll
                    ? [datum]
                    : [
                        ...state.selectedItems.filter(
                          (v) => getValueByKey(v, optionValueProp) !== "all"
                        ),
                        datum,
                      ];

                  onChange(
                    isAll
                      ? ["all"]
                      : [
                          ...value.filter(
                            (v) => (isAllVisible ? v !== "all" : true)
                          ),
                          getValueByKey(datum, optionValueProp),
                        ],
                    selectedItems
                  );
                  setState((st) => {
                    return {
                      ...st,
                      selectedItems,
                    };
                  });
                }
              } else {
                const selectedItems = datum;
                onChange(
                  getValueByKey(datum, optionValueProp),
                  pick(extraData, datum)
                );
                setState((st) => ({
                  ...st,
                  selectedItems,
                  dropdownVisible: false,
                }));
              }
            };

            return (
              <Menu.Item
                style={{
                  ...style,
                  width: "100%",
                  // height: 50,
                  // pointerEvents: "auto",
                  // zIndex: 9999,
                }}
                // disabled={isItemDisabled ? isItemDisabled(datum) : false}
                key={`${getValueByKey(datum, optionValueProp)}-${index}`}
                className={`multi-select-item ${checked ? "checked" : ""} ${
                  showCheckboxes ? "with-checkbox" : ""
                }`}
                onMouseDown={function(e) {
                  // e.preventDefault();
                  // e.stopPropagation();
                  onItemChange();
                }}
              >
                {showCheckboxes && (
                  <Checkbox checked={checked} onChange={onItemChange} />
                )}
                <span className="multi-select-item-title">
                  {getValueByKey(datum, optionLabelProp)}
                </span>
                {checked && !showCheckboxes ? <CheckOutlined /> : null}
              </Menu.Item>
            );
          }}
        </VariableSizeList>
      }
    >
      <div
        data-testid={id}
        className={`multi-select ${disabled ? "disabled" : ""} ${customClass}`}
      >
        <div className="tags">
          {multiple ? (
            value[0] === "all" ? (
              <Tag
                className="select-item-tag single"
                closable={!disabled}
                onClose={() => {
                  onChange([], []);
                  setState((st) => ({ ...st, selectedItems: [] }));
                }}
              >
                All
              </Tag>
            ) : (
              <>
                {[...(state.selectedItems || [])]
                  .slice(0, Math.min(maxTags, state.selectedItems.length))
                  .map((item) => {
                    return (
                      <Tooltip
                        key={getValueByKey(item, optionValueProp)}
                        title={getValueByKey(item, optionLabelProp)}
                      >
                        <Tag
                          className="select-item-tag"
                          key={item?.value}
                          closable={!disabled}
                          onClose={() => {
                            const selectedItems = state.selectedItems.filter(
                              (v) =>
                                getValueByKey(v, optionValueProp) !==
                                getValueByKey(item, optionValueProp)
                            );
                            onChange(
                              value.filter(
                                (v) =>
                                  v !== getValueByKey(item, optionValueProp)
                              ),
                              selectedItems
                            );
                            setState((st) => ({
                              ...st,
                              selectedItems,
                            }));
                          }}
                        >
                          {ellipsize(getValueByKey(item, optionLabelProp), 10)}
                        </Tag>
                      </Tooltip>
                    );
                  })}
                {state.selectedItems.length > maxTags && (
                  <Tooltip
                    title={`${state.selectedItems.length - maxTags} Item${
                      state.selectedItems.length > maxTags + 1 ? "s" : ""
                    } More`}
                  >
                    <Tag>+{state.selectedItems.length - maxTags}</Tag>
                  </Tooltip>
                )}
              </>
            )
          ) : state.focused ? (
            ""
          ) : value === "all" ? (
            <span>All</span>
          ) : value === "" || value === null ? (
            <span className="single-selected-item placeholder">
              {placeholder}
            </span>
          ) : (
            <span className="single-selected-item">
              {pathOr("", ["selectedItems", optionLabelProp], state)}
            </span>
          )}
          {showSearch && (
            <Input
              disabled={disabled}
              placeholder={
                state.focused && !multiple
                  ? pathOr("", ["selectedItems", optionLabelProp], state)
                  : (multiple && value?.length > 0) ||
                    (!multiple && value !== "")
                    ? ""
                    : placeholder
              }
              ref={inputRef}
              onFocus={() => {
                setState((st) => ({ ...st, focused: true }));
              }}
              onBlur={() => {
                onBlur && onBlur();
                setState((st) => ({
                  ...st,
                  focused: false,
                }));
              }}
              value={state.searchQuery}
              className={`multi-select-input ${
                multiple ? "multiple" : "single"
              } ${value?.length > 0 ? "" : "empty"}`}
              onChange={handleSearch}
            />
          )}
        </div>
        {allowClear && isValuePresent ? (
          <SmallCloseIcon
            onClick={() => {
              onChange(multiple ? [] : "", []);
            }}
          />
        ) : state.focused && !multiple ? (
          <SearchIcon />
        ) : state.dropdownVisible ? (
          <UpOutlined />
        ) : (
          <DownOutlined />
        )}
      </div>
    </Dropdown>
  );
};

export const FormikMultiSelect = ({ name, onChange, ...props }) => {
  const [{ value }, { touched, error }, { setValue, setTouched }] = useField(
    name
  );

  const { validateForm } = useFormikContext();
  const handleChange = useCallback(
    (value, selectedItems) => {
      setValue(value);
      setTouched(true);
      if (onChange) onChange(value, selectedItems);
      setTimeout(validateForm, 0);
    },
    [setValue, onChange, setTouched, validateForm]
  );

  const customClass = props.customClass || "";
  return (
    <CustomMultiSelect
      {...props}
      onChange={handleChange}
      value={value || []}
      customClass={`${customClass} ${error && touched ? "error-box" : ""}`}
      onBlur={() => setTouched(true)}
    />
  );
};
export default CustomMultiSelect;
